import { useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import type { Lang } from '../../lib/i18n'
import { changeDeliveryFee, HttpError, type OrderDetail } from '../../lib/orders-api'
import { formatMoney } from './format'
import { Ltr } from './Ltr'
import { ot } from './orders-i18n'

interface Props {
  trigger: React.ReactNode
  order: OrderDetail
  lang: Lang
  isOnline: boolean
  onSuccess: () => void
}

type Reason = 'combined' | 'other'

const FEE_PATTERN = /^\d+(\.\d{1,2})?$/

// Maps the server's plain-text 400 message (orders.service / order-fee.service throw
// `new BadRequestException('<text>')`, which the global filter wraps as { error: { code:
// 'UNKNOWN', message: '<text>' } } — no distinct code per case) to a localized line.
function mapServerError(lang: Lang, message: string): string {
  const m = message.toLowerCase()
  if (m.includes('no order with that code')) return ot(lang, 'feeChangeErrorNoOrder')
  if (m.includes('more than one order')) return ot(lang, 'feeChangeErrorMultipleOrders')
  if (m.includes('belongs to another customer')) return ot(lang, 'feeChangeErrorOtherCustomer')
  if (m.includes('no change')) return ot(lang, 'feeChangeErrorNoChange')
  if (m.includes('pickup order has no delivery fee')) return ot(lang, 'feeChangeErrorPickup')
  if (m.includes('delivery fee cannot be changed')) return ot(lang, 'feeChangeErrorWrongStatus')
  // R562: the two named refusals on a delivered order (order-fee.service.ts).
  if (m.includes('the invoice has posted')) return ot(lang, 'feeChangeErrorInvoicePosted')
  if (m.includes('the invoice post failed')) return ot(lang, 'feeChangeErrorInvoiceFailed')
  if (m.includes('no held invoice to correct')) return ot(lang, 'feeChangeErrorNoHeldInvoice')
  return ot(lang, 'feeChangeErrorGeneric')
}

export function FeeChangeDialog({ trigger, order, lang, isOnline, onSuccess }: Props) {
  const [open, setOpen] = useState(false)
  const [fee, setFee] = useState('')
  const [reason, setReason] = useState<Reason>('other')
  const [linkedOrderCode, setLinkedOrderCode] = useState('')
  const [note, setNote] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stillCharges, setStillCharges] = useState<{ code: string; fee: string } | null>(null)
  const [done, setDone] = useState(false)

  const reset = () => {
    setFee('')
    setReason('other')
    setLinkedOrderCode('')
    setNote('')
    setPending(false)
    setError(null)
    setStillCharges(null)
    setDone(false)
  }

  // Every close path — Done, Cancel, Escape, outside click — runs through here, so the
  // refresh fires once the dialog has actually closed, never while the success view is
  // still the thing on screen.
  const handleClose = () => {
    setOpen(false)
    if (done) onSuccess()
    reset()
  }

  // Not through enqueue(): the code lookup (reason='combined') needs the server, so this
  // write is online-only — offline, Save stays disabled rather than queuing a write the
  // dialog can't validate locally.
  const isValid =
    isOnline &&
    FEE_PATTERN.test(fee.trim()) &&
    (reason === 'other' ? note.trim().length > 0 : linkedOrderCode.trim().length > 0)

  const handleSave = async () => {
    if (!isValid) return
    setPending(true)
    setError(null)
    try {
      const result = await changeDeliveryFee(order.id, {
        fee: Number(fee),
        reason,
        ...(reason === 'combined' ? { linkedOrderCode: linkedOrderCode.trim() } : {}),
        ...(reason === 'other' ? { note: note.trim() } : {}),
      })
      setStillCharges(result.linkedOrderStillCharges ?? null)
      setDone(true)
    } catch (err) {
      setError(err instanceof HttpError ? mapServerError(lang, err.message) : ot(lang, 'feeChangeErrorGeneric'))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setOpen(true)
        } else {
          handleClose()
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle>{ot(lang, 'feeChangeTitle')}</DialogTitle>
          {/* R562: on a delivered order the dialog says plainly that this corrects a delivered
              order's fee before its invoice posts. */}
          {!done && (
            <DialogDescription data-fee-change-body={order.status === 'DELIVERED' ? 'post-delivery' : 'before-delivery'}>
              {ot(lang, order.status === 'DELIVERED' ? 'feeChangePostDeliveryBody' : 'feeChangeBody')}
            </DialogDescription>
          )}
        </DialogHeader>

        {done ? (
          <div className="space-y-2">
            <p className="text-sm text-foreground">{ot(lang, 'feeChangeSuccess')}</p>
            {stillCharges && (
              <p className="text-sm text-muted-foreground">
                <Ltr>{ot(lang, 'feeChangeStillCharges')(stillCharges.code, formatMoney(stillCharges.fee, order.currency))}</Ltr>
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label htmlFor="fee-change-amount" className="mb-1 block text-sm font-medium text-foreground">
                {ot(lang, 'feeChangeAmountLabel')}
              </label>
              <Input
                id="fee-change-amount"
                dir="ltr"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={fee}
                disabled={pending}
                onChange={(e) => setFee(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="fee-change-reason" className="mb-1 block text-sm font-medium text-foreground">
                {ot(lang, 'feeChangeReasonLabel')}
              </label>
              <select
                id="fee-change-reason"
                value={reason}
                disabled={pending}
                onChange={(e) => setReason(e.target.value as Reason)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:text-sm"
              >
                <option value="other">{ot(lang, 'feeChangeReasonOther')}</option>
                <option value="combined">{ot(lang, 'feeChangeReasonCombined')}</option>
              </select>
            </div>

            {reason === 'combined' && (
              <div>
                <label htmlFor="fee-change-linked-code" className="mb-1 block text-sm font-medium text-foreground">
                  {ot(lang, 'feeChangeLinkedOrderCodeLabel')}
                </label>
                <Input
                  id="fee-change-linked-code"
                  dir="ltr"
                  placeholder={ot(lang, 'feeChangeLinkedOrderCodePlaceholder')}
                  value={linkedOrderCode}
                  disabled={pending}
                  onChange={(e) => setLinkedOrderCode(e.target.value)}
                />
              </div>
            )}

            {reason === 'other' && (
              <div>
                <label htmlFor="fee-change-note" className="mb-1 block text-sm font-medium text-foreground">
                  {ot(lang, 'feeChangeNoteLabel')}
                </label>
                <Input
                  id="fee-change-note"
                  value={note}
                  disabled={pending}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter className="gap-2">
          {done ? (
            <Button size="lg" className="min-h-[48px]" onClick={handleClose}>
              {ot(lang, 'feeChangeDone')}
            </Button>
          ) : (
            <>
              <Button variant="outline" size="lg" className="min-h-[48px]" disabled={pending} onClick={handleClose}>
                {ot(lang, 'feeChangeCancel')}
              </Button>
              <Button size="lg" className="min-h-[48px]" disabled={pending || !isValid} onClick={handleSave}>
                {pending ? ot(lang, 'feeChangeSaving') : ot(lang, 'feeChangeSave')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
